import React, { useState, useEffect } from "react";
import { Layout, Typography, message, Spin, ConfigProvider, theme, Space } from "antd";
import AddressForm from "./components/AddressForm";
import ResultTable from "./components/ResultTable";
import { analyzeAddresses } from "./api";
import { ThunderboltOutlined, TwitterOutlined } from '@ant-design/icons';

const { Header, Content, Footer } = Layout;
const { Title, Text, Link } = Typography;

// 自定义暗色主题配置
const techyTheme = {
  algorithm: theme.darkAlgorithm,
  token: {
    colorPrimary: '#00A8E8', // 科技蓝
    colorInfo: '#00A8E8',
    colorSuccess: '#00BFA5', // 科技青
    colorWarning: '#FFC107',
    colorError: '#F44336',
    colorTextBase: '#E0E0E0',
    colorBgBase: '#121212', // 更深的背景
    colorBgContainer: '#1E1E1E', // 组件容器背景
    colorBgLayout: '#0A0A0A', // 整体布局背景
    fontFamily: 'Rajdhani, sans-serif',
    borderRadius: 4, // 可以调整圆角大小，更锐利或更圆润
    // 可以进一步自定义组件级别的 token
    // Card
    Card: {
      colorBgContainer: '#181818', // 卡片背景
      actionsBg: '#181818',
      colorBorderSecondary: '#333333',
    },
    // Table
    Table: {
      colorBgContainer: '#181818',
      headerBg: '#222222',
      headerColor: '#00A8E8', // 表头文字颜色
      borderColor: '#333333',
      rowHoverBg: '#282828',
    },
    // Input
    Input: {
      colorBgContainer: '#252525',
      colorBorder: '#444444',
      hoverBorderColor: '#00A8E8',
    },
    // Button
    Button: {
      colorPrimary: '#00A8E8',
      colorPrimaryHover: '#00BFFF',
      colorLink: '#00A8E8',
      colorLinkHover: '#00BFFF',
      defaultBg: '#252525',
      defaultBorderColor: '#444444',
      defaultColor: '#E0E0E0',
    },
    // Statistic
    Statistic: {
      titleFontSize: '1em',
      contentFontSize: '1.5em',
    }
  },
};

function App() {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [failedAddresses, setFailedAddresses] = useState([]);
  const [noTxAddresses, setNoTxAddresses] = useState([]); // 新增：存储今日未交易的地址
  const [isDarkMode, setIsDarkMode] = useState(true); // 假设默认开启暗黑模式

  // 如果需要切换主题的功能，可以添加一个函数来改变 isDarkMode
  // const toggleTheme = () => setIsDarkMode(!isDarkMode);

  const handleQuery = async (addresses) => {
    setLoading(true);
    try {
      const response = await analyzeAddresses(addresses);
      setResults(response);
      
      // 处理失败的地址
      const failures = response.filter(r => !r.success);
      setFailedAddresses(failures);
      
      // 处理今日未交易的地址
      const today = new Date().toISOString().split('T')[0];
      const noTransactions = response.filter(r => {
        return r.success && (!r.stats || !r.stats[today] || !r.stats[today].transactions || r.stats[today].transactions.length === 0);
      });
      setNoTxAddresses(noTransactions);
      
      // 显示相应的消息
      if (failures.length > 0) {
        message.warning({
          content: `${failures.length}个地址查询失败，可以点击重试`,
          style: { marginTop: '20vh' },
        });
      } else if (response.length > 0) {
        if (noTransactions.length > 0) {
          message.info({
            content: `查询成功! ${noTransactions.length}个地址今日尚未交易`,
            style: { marginTop: '20vh' },
          });
        } else {
          message.success({
            content: '查询成功!',
            style: { marginTop: '20vh' },
          });
        }
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message;
      message.error({
        content: "查询失败: " + errorMsg,
        style: { marginTop: '20vh' },
      });
    } finally {
      setLoading(false);
    }
  };

  // 应用全局样式，使 Spin 居中更美观
  useEffect(() => {
    const style = document.createElement('style');
    style.innerHTML = `
      .app-loading-spin {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: 9999;
      }
      .ant-layout-header {
        border-bottom: 1px solid ${techyTheme.token.colorPrimary}50; /* 头部底边框，增加科技感线条 */
        background: ${techyTheme.token.colorBgLayout} !important; /* 确保背景色生效 */
      }
      .ant-layout-content {
        background: linear-gradient(180deg, ${techyTheme.token.colorBgLayout} 0%, ${techyTheme.token.colorBgBase} 100%);
      }
      .ant-layout-footer {
        background: ${techyTheme.token.colorBgLayout} !important;
        border-top: 1px solid ${techyTheme.token.colorPrimary}30;
        text-align: center;
        color: #555;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);


  return (
    <ConfigProvider theme={isDarkMode ? techyTheme : { algorithm: theme.defaultAlgorithm, token: { fontFamily: 'Rajdhani, sans-serif' } }}>
      <Layout style={{ minHeight: "100vh", backgroundColor: techyTheme.token.colorBgLayout }}>
        <Content style={{ padding: '24px 32px', }}>
          <AddressForm 
            onQuery={handleQuery} 
            failedAddresses={failedAddresses}
            setFailedAddresses={setFailedAddresses}
            noTxAddresses={noTxAddresses}
            loading={loading}
          />
          {loading && (
            <div className="app-loading-spin">
              <Spin size="large" tip={<Text style={{color: techyTheme.token.colorPrimary, marginTop: 10, fontSize: '1.2em'}}>分析中...</Text>} />
            </div>
          )}
          {!loading && <ResultTable results={results} />}
        </Content>
        <Footer>
          ©{new Date().getFullYear()} - X paidaxing5
        </Footer>
      </Layout>
    </ConfigProvider>
  );
}

export default App;
